import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Gift, Clock, ToggleLeft, Shield, Unlock, Search, RefreshCw } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";

const DEFAULT_PRIZES = [
  { id: "p1", label: "৳10", amount: 10, weight: 30, color: "#10b981" },
  { id: "p2", label: "৳25", amount: 25, weight: 25, color: "#f59e0b" },
  { id: "p3", label: "৳50", amount: 50, weight: 15, color: "#3b82f6" },
  { id: "p4", label: "৳100", amount: 100, weight: 10, color: "#8b5cf6" },
  { id: "p5", label: "৳250", amount: 250, weight: 5, color: "#ec4899" },
  { id: "p6", label: "৳500", amount: 500, weight: 2, color: "#ef4444" },
  { id: "p7", label: "৳5", amount: 5, weight: 35, color: "#14b8a6" },
  { id: "p8", label: "Try Again", amount: 0, weight: 40, color: "#6b7280" },
];

export default function FreeSpin() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cooldowns, setCooldowns] = useState([]);
  const [cdSearch, setCdSearch] = useState("");
  const [cdLoading, setCdLoading] = useState(false);
  const cdPaged = usePagedRows(cooldowns, 12, [cdSearch]);

  const loadCooldowns = useCallback(async (q = "") => {
    setCdLoading(true);
    try {
      const { data } = await api.get("/free-spin/device-cooldowns", {
        params: q ? { search: q } : {},
      });
      setCooldowns(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cooldowns");
    } finally {
      setCdLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setForm({
          free_spin_enabled: data.free_spin_enabled !== false,
          free_spin_cooldown_hours: data.free_spin_cooldown_hours ?? 24,
          free_spin_apply_turnover: data.free_spin_apply_turnover !== false,
          free_spin_turnover_multiplier: data.free_spin_turnover_multiplier ?? 2,
          free_spin_prizes: Array.isArray(data.free_spin_prizes) && data.free_spin_prizes.length > 0
            ? data.free_spin_prizes
            : DEFAULT_PRIZES,
        });
      } catch (e) {
        toast.error("Failed to load settings");
      }
    })();
    loadCooldowns();
  }, [loadCooldowns]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrize = (i, k, v) =>
    setForm((f) => ({
      ...f,
      free_spin_prizes: f.free_spin_prizes.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)),
    }));

  const addPrize = () =>
    setForm((f) => ({
      ...f,
      free_spin_prizes: [
        ...f.free_spin_prizes,
        { id: `p${Date.now()}`, label: "৳0", amount: 0, weight: 10, color: "#64748b" },
      ],
    }));

  const removePrize = (i) =>
    setForm((f) => ({
      ...f,
      free_spin_prizes: f.free_spin_prizes.filter((_, idx) => idx !== i),
    }));

  const resetDefaults = () => {
    if (!window.confirm("Reset prize list to defaults?")) return;
    setForm((f) => ({ ...f, free_spin_prizes: DEFAULT_PRIZES }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        free_spin_enabled: !!form.free_spin_enabled,
        free_spin_cooldown_hours: parseFloat(form.free_spin_cooldown_hours) || 24,
        free_spin_apply_turnover: !!form.free_spin_apply_turnover,
        free_spin_turnover_multiplier: parseFloat(form.free_spin_turnover_multiplier) || 2,
        free_spin_prizes: form.free_spin_prizes.map((p) => ({
          id: p.id,
          label: String(p.label || ""),
          amount: parseFloat(p.amount) || 0,
          weight: parseFloat(p.weight) || 0,
          color: p.color || "#64748b",
        })),
      };
      await api.put("/settings", payload);
      toast.success("Free spin settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;

  const totalWeight = form.free_spin_prizes.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0) || 1;

  const unblockOne = async (id, label) => {
    if (!window.confirm(`Unblock this cooldown record${label ? ` (${label})` : ""}?`)) return;
    try {
      await api.delete(`/free-spin/device-cooldowns/${id}`);
      toast.success("Cooldown removed — user can spin again");
      setCooldowns((rows) => rows.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unblock failed");
    }
  };

  const bulkUnblock = async (key, value) => {
    if (!window.confirm(`Unblock ALL cooldowns for ${key}=${value}?`)) return;
    try {
      const { data } = await api.post("/free-spin/device-cooldowns/unblock", { [key]: value });
      toast.success(`Removed ${data.removed || 0} cooldown record(s)`);
      loadCooldowns(cdSearch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk unblock failed");
    }
  };

  const fmtRemaining = (s) => {
    if (s <= 0) return "—";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${m}m`;
  };

  return (
    <div data-testid="admin-free-spin-page">
      <PageHeader title="Daily Free Spin" subtitle="Control the lucky wheel · cooldown · prizes · probabilities" />

      <div className="space-y-5 max-w-5xl">
        <Section title="General" icon={ToggleLeft}>
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50 mb-4">
            <input
              type="checkbox"
              checked={form.free_spin_enabled}
              onChange={(e) => set("free_spin_enabled", e.target.checked)}
              data-testid="spin-enabled-toggle"
              className="h-4 w-4"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold text-gray-900">
                {form.free_spin_enabled ? "Daily Free Spin is ON" : "Daily Free Spin is OFF"}
              </div>
              <div className="text-xs text-gray-500">
                {form.free_spin_enabled
                  ? "Users can spin the wheel once per cooldown period and win a bonus."
                  : "Free spin is hidden from users."}
              </div>
            </div>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                form.free_spin_enabled
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-gray-100 text-gray-600 border border-gray-200"
              }`}
            >
              {form.free_spin_enabled ? "ON" : "OFF"}
            </span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Cooldown (hours)">
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={form.free_spin_cooldown_hours}
                onChange={(e) => set("free_spin_cooldown_hours", e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                data-testid="spin-cooldown-input"
              />
            </Field>
            <Field label="Apply Turnover to Winnings">
              <label className="flex items-center gap-2 h-10 px-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={form.free_spin_apply_turnover}
                  onChange={(e) => set("free_spin_apply_turnover", e.target.checked)}
                  data-testid="spin-turnover-toggle"
                />
                <span className="text-sm">
                  {form.free_spin_apply_turnover ? "Yes — prize counts as bonus" : "No — instant withdrawable"}
                </span>
              </label>
            </Field>
            <Field label="Turnover Multiplier (×)">
              <input
                type="number"
                step="0.1"
                min="0"
                value={form.free_spin_turnover_multiplier}
                onChange={(e) => set("free_spin_turnover_multiplier", e.target.value)}
                disabled={!form.free_spin_apply_turnover}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular disabled:opacity-50 disabled:bg-gray-50"
                data-testid="spin-turnover-mult-input"
              />
            </Field>
          </div>
        </Section>

        <Section title="Prizes & Probabilities" icon={Gift}>
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-gray-500">
              The wheel draws a prize using weighted random. Higher weight = higher chance. Current total weight: <span className="font-semibold tabular">{totalWeight}</span>
            </p>
            <div className="flex gap-2">
              <button
                onClick={resetDefaults}
                type="button"
                className="h-9 px-3 border border-gray-200 hover:bg-gray-50 rounded-sm text-xs inline-flex items-center gap-1"
                data-testid="spin-reset-defaults-btn"
              >
                Reset Defaults
              </button>
              <button
                onClick={addPrize}
                type="button"
                className="h-9 px-3 bg-slate-800 hover:bg-slate-900 text-white rounded-sm text-xs inline-flex items-center gap-1"
                data-testid="spin-add-prize-btn"
              >
                <Plus size={12} /> Add Prize
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-3 py-2 w-14">#</th>
                  <th className="px-3 py-2">Label</th>
                  <th className="px-3 py-2">Amount (৳)</th>
                  <th className="px-3 py-2">Weight</th>
                  <th className="px-3 py-2">Probability</th>
                  <th className="px-3 py-2">Color</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {form.free_spin_prizes.map((p, i) => {
                  const prob = (((parseFloat(p.weight) || 0) / totalWeight) * 100).toFixed(1);
                  return (
                    <tr key={p.id || i} className="hover:bg-gray-50" data-testid={`spin-prize-row-${i}`}>
                      <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                      <td className="px-3 py-2">
                        <input
                          value={p.label}
                          onChange={(e) => setPrize(i, "label", e.target.value)}
                          className="w-full h-9 px-2 border border-gray-200 rounded-sm text-sm"
                          data-testid={`spin-prize-label-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={p.amount}
                          onChange={(e) => setPrize(i, "amount", e.target.value)}
                          className="w-28 h-9 px-2 border border-gray-200 rounded-sm text-sm tabular"
                          data-testid={`spin-prize-amount-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={p.weight}
                          onChange={(e) => setPrize(i, "weight", e.target.value)}
                          className="w-24 h-9 px-2 border border-gray-200 rounded-sm text-sm tabular"
                          data-testid={`spin-prize-weight-${i}`}
                        />
                      </td>
                      <td className="px-3 py-2 tabular">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${prob}%` }} />
                          </div>
                          <span className="text-xs text-gray-600 w-12">{prob}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={p.color}
                            onChange={(e) => setPrize(i, "color", e.target.value)}
                            className="h-9 w-12 border border-gray-200 rounded-sm cursor-pointer"
                          />
                          <input
                            value={p.color}
                            onChange={(e) => setPrize(i, "color", e.target.value)}
                            className="w-24 h-9 px-2 border border-gray-200 rounded-sm text-xs tabular"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => removePrize(i)}
                          type="button"
                          className="h-8 w-8 inline-flex items-center justify-center border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-sm"
                          data-testid={`spin-prize-remove-${i}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="Device / IP Cooldowns (Anti-Abuse)" icon={Shield}>
          <div className="text-xs text-gray-600 mb-3">
            Each successful free spin locks the user&apos;s device and IP so they can&apos;t logout + register a new account to claim another spin. You can manually unblock here if a genuine user got blocked.
          </div>

          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={cdSearch}
                onChange={(e) => setCdSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadCooldowns(cdSearch)}
                placeholder="Search by username, device_id, or IP..."
                className="w-full h-9 pl-9 pr-3 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                data-testid="spin-cd-search"
              />
            </div>
            <button
              onClick={() => loadCooldowns(cdSearch)}
              className="h-9 px-3 border border-gray-300 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50"
              data-testid="spin-cd-refresh"
            >
              <RefreshCw size={13} className={cdLoading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Username</th>
                  <th className="px-3 py-2 text-left font-semibold">Device ID</th>
                  <th className="px-3 py-2 text-left font-semibold">IP</th>
                  <th className="px-3 py-2 text-left font-semibold">Last Spin</th>
                  <th className="px-3 py-2 text-left font-semibold">Status</th>
                  <th className="px-3 py-2 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cooldowns.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-gray-400 text-sm">
                      No cooldown records found.
                    </td>
                  </tr>
                )}
                {cdPaged.visible.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50" data-testid={`spin-cd-row-${c.id}`}>
                    <td className="px-3 py-2 font-medium text-gray-800">{c.username || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-600">
                      {c.device_id ? (
                        <button
                          onClick={() => bulkUnblock("device_id", c.device_id)}
                          className="hover:text-emerald-600 underline decoration-dotted"
                          title="Bulk unblock this device"
                        >
                          {c.device_id.length > 18 ? c.device_id.slice(0, 18) + "…" : c.device_id}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-600">
                      {c.ip ? (
                        <button
                          onClick={() => bulkUnblock("ip", c.ip)}
                          className="hover:text-emerald-600 underline decoration-dotted"
                          title="Bulk unblock this IP"
                        >
                          {c.ip}
                        </button>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-[12px] text-gray-600 whitespace-nowrap">
                      {c.last_spin_at ? new Date(c.last_spin_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {c.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold">
                          <Clock size={10} /> {fmtRemaining(c.seconds_remaining)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-semibold">
                          Expired
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => unblockOne(c.id, c.username || c.device_id || c.ip)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-sm text-[12px] font-semibold"
                        data-testid={`spin-cd-unblock-${c.id}`}
                      >
                        <Unlock size={12} /> Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination total={cdPaged.totalRows} page={cdPaged.page} pageSize={cdPaged.pageSize} onChange={cdPaged.setPage} testIdPrefix="spin-cd-pg" />

          <div className="mt-3 text-[11px] text-gray-500">
            Tip: Click a device ID or IP to bulk-unblock all records sharing that key.
          </div>
        </Section>

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="spin-save-btn"
          >
            <Save size={14} /> {saving ? "Saving..." : "Save Free Spin Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        {Icon && <Icon size={14} className="text-emerald-600" />}
        <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{title}</div>
      </div>
      {children}
    </div>
  );
}
